import { useState } from 'react';
import { IconTooltip } from '@/components/common/IconTooltip';
import {
  DotsThreeIcon,
  PencilSimpleIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { WorkbookOutput } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import type { WorkbookEdits } from './workbookEdits';

/** Rename or delete one Output: both are Workbook edits, saved and versioned with it. */
export function WorkbookOutputMenu({
  output,
  edits,
  disabled,
}: {
  output: WorkbookOutput;
  edits: WorkbookEdits;
  disabled: boolean;
}) {
  const gt = useGT();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(output.name);
  const handle = edits.output(output.id);
  const optionsLabel = gt('{name} options', {
    context: 'workbook output menu',
    name: output.name,
  });
  function rename() {
    const next = name.trim().slice(0, 200);
    if (next && next !== output.name)
      handle.update((latest) => ({ ...latest, name: next }));
    setRenaming(false);
  }
  return (
    <>
      <DropdownMenu>
        <IconTooltip label={optionsLabel}>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="size-6 shrink-0"
              disabled={disabled}
              aria-label={optionsLabel}
            >
              <DotsThreeIcon className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
        </IconTooltip>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            onClick={() => {
              setName(output.name);
              setRenaming(true);
            }}
          >
            <PencilSimpleIcon />
            <T context="workbook output menu">Rename</T>
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" onClick={handle.remove}>
            <TrashIcon />
            <T context="workbook output menu">Delete</T>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={renaming} onOpenChange={setRenaming}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              <T context="workbook rename output">Rename output</T>
            </DialogTitle>
          </DialogHeader>
          <form
            id="workbook-rename-output"
            onSubmit={(event) => {
              event.preventDefault();
              rename();
            }}
          >
            <Input
              value={name}
              maxLength={200}
              aria-label={gt('Output name', {
                context: 'workbook rename output',
              })}
              onChange={(event) => setName(event.target.value)}
            />
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(false)}>
              <T context="workbook rename output cancel">Cancel</T>
            </Button>
            <Button
              type="submit"
              form="workbook-rename-output"
              disabled={!name.trim()}
            >
              <T context="workbook rename output save">Rename</T>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
